import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles, tokens, mergeClasses,
  Body1, Caption1, Spinner, Button, SearchBox,
  DataGrid, DataGridHeader, DataGridHeaderCell, DataGridBody, DataGridRow, DataGridCell,
  createTableColumn,
  type TableColumnDefinition,
} from '@fluentui/react-components';
import { FolderRegular } from '@fluentui/react-icons';
import { useMyProjects } from '../../api/hooks';
import { HttpError } from '../../api/client';
import { brandColors, brandFonts } from '../../theme/brandColors';
import type { Project } from '../../api/types';

/* EXECUTIVE-BRIEFING design, matching EpicListPage. Navy hero (kicker → H1 →
   Active/Inactive bars + project-count composite), then a white card holding the
   project table: navy Plus-Jakarta names, mono metadata, soft status pills,
   clickable rows → that project's epics. All four states implemented. */

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL, width: '100%', fontFamily: brandFonts.body },

  // ── Hero ──
  hero: {
    backgroundColor: brandColors.navy,
    color: brandColors.white,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '2.125rem 2.375rem 2rem',
    boxShadow: tokens.shadow28,
    overflow: 'hidden',
  },
  kicker: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    color: brandColors.lightBlue,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    marginBottom: tokens.spacingVerticalM,
  },
  heroTitle: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '-0.01em',
    margin: 0,
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1.45fr 1fr',
    gap: tokens.spacingHorizontalXXXL,
    alignItems: 'center',
    marginTop: tokens.spacingVerticalXXL,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr', rowGap: tokens.spacingVerticalXL },
  },
  heroBars: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  barRow: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  barTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  barLabel: { fontSize: tokens.fontSizeBase300, color: 'rgba(255,255,255,0.92)' },
  barVal: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },
  heroTrack: { height: '0.375rem', borderRadius: tokens.borderRadiusCircular, backgroundColor: 'rgba(255,255,255,0.12)', overflow: 'hidden' },
  heroFill: { height: '100%', borderRadius: tokens.borderRadiusCircular },
  composite: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXS,
    borderLeft: '1px solid rgba(255,255,255,0.14)', paddingLeft: tokens.spacingHorizontalXXXL,
    '@media (max-width: 860px)': { borderLeft: 'none', paddingLeft: 0 },
  },
  compositeLabel: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: brandColors.lightBlue,
  },
  compositeNum: { fontFamily: brandFonts.heading, fontSize: '3.5rem', fontWeight: tokens.fontWeightBold, lineHeight: '1', color: brandColors.white },
  compositeMeta: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },

  // ── Card + table ──
  container: {
    display: 'flex', flexDirection: 'column',
    backgroundColor: brandColors.white,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${brandColors.greyLine}`,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM, padding: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${brandColors.greyLine}`,
  },
  search: { width: '24rem', maxWidth: '50%' },
  gridWrap: {
    overflow: 'auto',
    '& [role="row"] > :first-child': { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
    '& [role="row"] > :nth-child(2)': { flexGrow: 0, flexShrink: 0, flexBasis: '9rem' },   // Status
    '& [role="row"] > :nth-child(3)': { flexGrow: 0, flexShrink: 0, flexBasis: '12rem' },  // Last synced
    '& [role="row"] > :nth-child(4)': { flexGrow: 0, flexShrink: 0, flexBasis: '12rem' },  // Target
    '& [role="columnheader"]': {
      fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.1em', textTransform: 'uppercase', color: brandColors.greyText,
    },
  },
  rowClickable: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    '&:focus-visible': { outline: `2px solid ${brandColors.blue}`, outlineOffset: '-2px' },
  },
  titleCell: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  titleText: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.01em', color: brandColors.navy,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  idText: {
    fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, letterSpacing: '0.02em', color: brandColors.greyText,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  metaText: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: brandColors.greyText },
  pill: {
    display: 'inline-flex', alignItems: 'center', borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${brandColors.greyLine}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.1em', textTransform: 'uppercase', color: brandColors.greyText,
  },
  emptyOrError: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalXXXL, textAlign: 'center',
    color: tokens.colorNeutralForeground2,
  },
});

export function ReportsIndex() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading, error, refetch } = useMyProjects();

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return q ? data.filter((p) => p.name.toLowerCase().includes(q)) : data;
  }, [data, search]);

  const total = data?.length ?? 0;
  const activeCount = useMemo(() => filtered.filter((p) => p.isActive).length, [filtered]);
  const shownTotal = filtered.length || 1;
  const breakdown = [
    { label: 'Active', count: activeCount, color: brandColors.green },
    { label: 'Inactive', count: filtered.length - activeCount, color: brandColors.greyText },
  ].filter((b) => b.count > 0);

  const columns: TableColumnDefinition<Project>[] = useMemo(() => [
    createTableColumn<Project>({
      columnId: 'name',
      compare: (a, b) => a.name.localeCompare(b.name),
      renderHeaderCell: () => 'Project',
      renderCell: (p) => (
        <div className={styles.titleCell}>
          <span className={styles.titleText}>{p.name}</span>
          <span className={styles.idText}>
            {p.plannedTargetUtc ? `Target ${formatDate(p.plannedTargetUtc)}` : 'Azure DevOps project'}
          </span>
        </div>
      ),
    }),
    createTableColumn<Project>({
      columnId: 'status',
      compare: (a, b) => Number(b.isActive) - Number(a.isActive),
      renderHeaderCell: () => 'Status',
      renderCell: (p) => {
        const c = p.isActive ? brandColors.green : brandColors.greyText;
        return (
          <span className={styles.pill} style={{ backgroundColor: `${c}29`, color: c }}>
            {p.isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    }),
    createTableColumn<Project>({
      columnId: 'synced',
      compare: (a, b) => (a.lastSyncedUtc ?? '').localeCompare(b.lastSyncedUtc ?? ''),
      renderHeaderCell: () => 'Last synced',
      renderCell: (p) => <span className={styles.metaText}>{p.lastSyncedUtc ? formatDate(p.lastSyncedUtc) : 'Never'}</span>,
    }),
    createTableColumn<Project>({
      columnId: 'target',
      compare: (a, b) => (a.plannedTargetUtc ?? '').localeCompare(b.plannedTargetUtc ?? ''),
      renderHeaderCell: () => 'Target',
      renderCell: (p) => <span className={styles.metaText}>{formatDate(p.plannedTargetUtc)}</span>,
    }),
  ], [styles]);

  return (
    <div className={styles.page}>
      {/* Navy executive hero */}
      <section className={styles.hero}>
        <div className={styles.kicker}><FolderRegular fontSize={14} aria-hidden /> Your Projects</div>
        <h1 className={styles.heroTitle}>Projects</h1>
        <div className={styles.heroGrid}>
          <div className={styles.heroBars}>
            {breakdown.map((b) => (
              <div key={b.label} className={styles.barRow}>
                <div className={styles.barTop}>
                  <span className={styles.barLabel}>{b.label}</span>
                  <span className={styles.barVal}>{b.count} · {Math.round((b.count / shownTotal) * 100)}%</span>
                </div>
                <div className={styles.heroTrack}>
                  <div className={styles.heroFill} style={{ width: `${(b.count / shownTotal) * 100}%`, backgroundColor: b.color }} />
                </div>
              </div>
            ))}
            {breakdown.length === 0 && <span className={styles.barLabel}>No projects to summarise.</span>}
          </div>
          <div className={styles.composite}>
            <span className={styles.compositeLabel}>Projects</span>
            <span className={styles.compositeNum}>{total}</span>
            <span className={styles.compositeMeta}>{activeCount} active · sourced from Azure DevOps</span>
          </div>
        </div>
      </section>

      <div className={styles.container} role="region" aria-label="Projects list">
        <div className={styles.toolbar}>
          <SearchBox
            appearance="underline"
            className={styles.search}
            placeholder="Search projects…"
            value={search}
            onChange={(_e, d) => setSearch(d.value)}
            aria-label="Search projects"
          />
        </div>

        {isLoading ? (
          <div className={styles.emptyOrError}><Spinner size="small" label="Loading your projects…" /></div>
        ) : error ? (
          <div className={styles.emptyOrError}>
            <Body1>
              {(error as HttpError).payload?.code === 'NOT_INVITED'
                ? 'Your Microsoft account is not yet invited to DevOps Reports. Contact an administrator.'
                : "Couldn't load your projects."}
            </Body1>
            {(error as HttpError).payload?.code !== 'NOT_INVITED' && (
              <Button appearance="primary" onClick={() => refetch()}>Try again</Button>
            )}
          </div>
        ) : total === 0 ? (
          <div className={styles.emptyOrError}>
            <FolderRegular fontSize={32} />
            <Body1>No projects accessible to you yet.</Body1>
            <Caption1>An administrator must register projects and grant you access.</Caption1>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyOrError}>
            <Body1>No projects match "{search}".</Body1>
            <Button appearance="subtle" onClick={() => setSearch('')}>Clear search</Button>
          </div>
        ) : (
          <div className={styles.gridWrap}>
            <DataGrid items={filtered} columns={columns} sortable focusMode="row_unstable" getRowId={(p) => p.id}>
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<Project>>
                {({ item, rowId }) => {
                  const go = () => navigate(`/reports/projects/${item.id}/epics`);
                  return (
                    <DataGridRow<Project>
                      key={rowId}
                      className={mergeClasses(styles.rowClickable)}
                      onClick={go}
                      onKeyDown={(ev: React.KeyboardEvent<HTMLDivElement>) => {
                        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); }
                      }}
                      aria-label={`Open ${item.name}`}
                    >
                      {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                    </DataGridRow>
                  );
                }}
              </DataGridBody>
            </DataGrid>
          </div>
        )}

        {!isLoading && !error && total > 0 && (
          <div className={styles.footer}>
            <span>
              {filtered.length === total
                ? `${total} project${total === 1 ? '' : 's'}`
                : `Showing ${filtered.length} of ${total} project${total === 1 ? '' : 's'}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
